import classNames from 'classnames';
import type moment from 'moment';
import React from 'react';
import type { GroupBase, OptionsOrGroups } from 'react-select';
import Button from '../../components/Button';
import DateRangePicker from '../../components/DateRangePicker';
import { FieldSet } from '../../components/Form';
import Modal, { ModalActionFooter, ModalTitle } from '../../components/Modal';
import Select from '../../components/Select';
import { AuditLogType } from '../../graphql/generated/graphqlTypes';
import type { EntitySearchResult } from '../../search/entitySearch/EntitySelect';
import EntitySelect from '../../search/entitySearch/EntitySelect';
import styles from './AuditFilter.module.scss';

type Option = { label: string; value: AuditLogType };

const ActionOptions: OptionsOrGroups<Option, GroupBase<Option>> = [
  {
    label: 'Authentication',
    options: [
      { label: 'Password login success', value: AuditLogType.LoginSuccess },
      { label: 'Password login failure', value: AuditLogType.LoginFailure },
      { label: 'SSO login success', value: AuditLogType.LoginSso },
      { label: 'Magic link login success', value: AuditLogType.LoginMagic },
      { label: 'Password reset success', value: AuditLogType.ResetSuccess },
      { label: 'Password reset request', value: AuditLogType.ResetRequest },
    ],
  },
  {
    label: 'User',
    options: [
      { label: 'User account update', value: AuditLogType.UserUpdate },
      { label: 'User account deactivate', value: AuditLogType.UserDeactivate },
      { label: 'User account reactivate', value: AuditLogType.UserReactivate },
      { label: 'User account create', value: AuditLogType.UserCreate },
    ],
  },
  {
    label: 'Integration',
    options: [
      { label: 'Integration install', value: AuditLogType.IntegrationAdd },
      { label: 'Integration update', value: AuditLogType.IntegrationUpdate },
      { label: 'Integration remove', value: AuditLogType.IntegrationDelete },
    ],
  },
  {
    label: 'Content',
    options: [
      { label: 'Post deleted', value: AuditLogType.PostDelete },
      { label: 'Topic deleted', value: AuditLogType.TopicDelete },
      { label: 'Group deleted', value: AuditLogType.GroupDelete },
      { label: 'Template deleted', value: AuditLogType.TemplateDelete },
    ],
  },
];

interface AuditFilterProps {
  action: AuditLogType | null;
  setAction: (action: AuditLogType | null) => void;
  entity: EntitySearchResult | null;
  setEntity: (entity: EntitySearchResult | null) => void;
  onDatesChange: (dates: {
    startAt: moment.Moment;
    endAt: moment.Moment;
  }) => void;
  asModal?: boolean;
  close?: () => void;
}

const AuditFilter = ({
  action,
  setAction,
  entity,
  setEntity,
  onDatesChange,
  asModal = false,
  close,
}: AuditFilterProps) => {
  const body = (
    <div
      className={classNames(styles.filter, { [styles.modalFilter]: asModal })}
    >
      <FieldSet legend="Action" margin="none">
        <Select
          options={ActionOptions}
          isClearable
          value={action}
          onChange={setAction}
          placeholder="Choose action"
        />
      </FieldSet>
      <FieldSet legend="User" margin="none">
        <EntitySelect
          value={entity}
          onChange={setEntity}
          config={{ user: { includeDeactivated: true } }}
          placeholder="Choose user"
          isClearable
        />
      </FieldSet>
      <FieldSet legend="Date" margin="none">
        <DateRangePicker onDatesChange={onDatesChange} />
      </FieldSet>
    </div>
  );

  if (asModal) {
    return (
      <Modal fullscreen>
        <ModalTitle close={close}>Filter</ModalTitle>
        {body}
        <ModalActionFooter
          primary={
            <Button variant="primary" block onClick={close}>
              Apply
            </Button>
          }
        />
      </Modal>
    );
  }

  return body;
};

export default AuditFilter;
